/**
 * Escrever `presentationSteps` no frame — marcar, desmarcar e reordenar.
 *
 * A decisão de QUAL índice mora em `passos.ts` (puro, testado sem build). Aqui fica só a
 * escrita: `generateKeyBetweenV2` e `crud.updateElement`, exatamente o par que
 * `frame-order-menu.ts:187-196` já usa para ordenar frames — um nível acima.
 *
 * ⚠️ **Toda escrita poda os órfãos junto.** A leitura já os filtra (`ordenarPassos`), então
 * isto não conserta defeito nenhum: é higiene, e roda **só quando alguém já está escrevendo**
 * naquele frame. Podar na leitura escreveria no documento de quem só está olhando — que é
 * exatamente o que a decisão 9.4 do dossiê proíbe para a apresentação.
 */

import type { EdgelessCRUDExtension } from '@blocksuite/affine-block-surface';
import type { FrameBlockModel } from '@blocksuite/affine-model';
import { generateKeyBetweenV2, type GfxModel } from '@blocksuite/std/gfx';

import {
  desmarcar,
  estaMarcado,
  lerPassos,
  marcar,
  ordenarPassos,
  type PassoResolvido,
  type Passos,
  podarOrfaos,
  vizinhosParaMarcarNoFim,
  vizinhosParaMover,
} from './passos';

type Crud = Pick<EdgelessCRUDExtension, 'updateElement'>;

/** Os passos deste frame, já ordenados e sem órfão. É a ÚNICA porta de leitura. */
export function passosDoFrame(frame: FrameBlockModel): PassoResolvido[] {
  return ordenarPassos(lerPassos(frame.props.presentationSteps), frame.childIds);
}

/** O elemento tem passo neste frame? */
export function elementoEstaMarcado(
  frame: FrameBlockModel,
  elementId: string
): boolean {
  return estaMarcado(lerPassos(frame.props.presentationSteps), elementId);
}

/**
 * O frame a que este elemento pertence, ou `null`.
 *
 * ⚠️ Um elemento tem **um** contêiner (`element.group`). Se ele está dentro de um
 * `affine:group` que está dentro do frame, o pai é o GRUPO — e é o grupo que aparece em
 * `frame.childIds`. Marcar um elemento agrupado individualmente não passa por aqui, e isso é
 * coerente com o produto: quem agrupou pediu que aquilo se comportasse como uma coisa só.
 */
export function frameDoElemento(elemento: GfxModel): FrameBlockModel | null {
  const container = (elemento as { group?: unknown }).group;
  if (!container) return null;
  const modelo = container as { flavour?: string };
  return modelo.flavour === 'affine:frame'
    ? (container as FrameBlockModel)
    : null;
}

/** Marca o elemento no FIM da fila do frame. Idempotente: já marcado não se mexe. */
export function marcarElemento(
  crud: Crud,
  frame: FrameBlockModel,
  elementId: string
): void {
  const atuais = lerPassos(frame.props.presentationSteps);
  if (estaMarcado(atuais, elementId)) return;

  const ordenados = ordenarPassos(atuais, frame.childIds);
  const { antes, depois } = vizinhosParaMarcarNoFim(ordenados);
  const novos = marcar(limpar(atuais, frame), elementId, generateKeyBetweenV2(antes, depois));

  crud.updateElement(frame.id, { presentationSteps: novos });
}

/** Tira o passo do elemento. Apaga a prop inteira quando era o último. */
export function desmarcarElemento(
  crud: Crud,
  frame: FrameBlockModel,
  elementId: string
): void {
  const atuais = lerPassos(frame.props.presentationSteps);
  if (!estaMarcado(atuais, elementId)) return;

  crud.updateElement(frame.id, {
    presentationSteps: desmarcar(limpar(atuais, frame), elementId),
  });
}

/**
 * Move um passo para a posição `destino` da lista.
 *
 * `destino` é o índice na lista JÁ SEM o item movido — a mesma convenção do
 * `frame-order-menu`, e o motivo de `vizinhosParaMover` existir separado e testado.
 */
export function moverPasso(
  crud: Crud,
  frame: FrameBlockModel,
  elementId: string,
  destino: number
): void {
  const atuais = lerPassos(frame.props.presentationSteps);
  if (!estaMarcado(atuais, elementId)) return;

  const ordenados = ordenarPassos(atuais, frame.childIds);
  const { antes, depois } = vizinhosParaMover(ordenados, elementId, destino);
  if (antes === depois) return; // já está lá

  crud.updateElement(frame.id, {
    presentationSteps: marcar(
      limpar(atuais, frame),
      elementId,
      generateKeyBetweenV2(antes, depois)
    ),
  });
}

/** Poda órfãos antes de qualquer escrita. `null` do `podarOrfaos` significa "nada a podar". */
function limpar(atuais: Passos | undefined, frame: FrameBlockModel): Passos | undefined {
  return podarOrfaos(atuais, frame.childIds) ?? atuais;
}
